import {
    AfterViewChecked,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnInit,
    Output,
    QueryList,
    Renderer2,
    ViewChild,
    ViewChildren,
} from '@angular/core';
import { INameId } from '@modeso/types__aumico-lib-core-be/dist/models/interfaces/INameId.interface';
import { Annotation } from '../../interfaces/annotation.interface';

@Component({
    selector: 'app-annotations',
    templateUrl: './annotations.component.html',
    styleUrls: ['./annotations.component.scss'],
})
export class AnnotationsComponent implements OnInit, AfterViewChecked {
    @Input() annotations: Annotation[] = [];
    @Input() currentUserId!: string;
    @Input() taskId!: string;
    @Input() projectId!: string;
    @Input() taskName: string = '';
    @Input() workflowArea?: string;
    @Input() users: INameId[] = [];
    @Input() disabled: boolean = false; // Disable all actions if true

    @Output() onAnnotationAdded = new EventEmitter<Annotation>();
    @Output() onAnnotationChanged = new EventEmitter<Annotation>();
    @Output() onAnnotationRemoved = new EventEmitter<Annotation>();

    @ViewChild('newAnnotationTextarea') newAnnotationTextarea!: ElementRef<HTMLTextAreaElement>;
    @ViewChildren('annotationText') annotationTextEls!: QueryList<ElementRef>;
    @ViewChild('editTextarea') editTextarea!: ElementRef<HTMLTextAreaElement>;

    editingId: string | null = null;
    newAnnotationFocused = false;
    showDeleteModal = false;
    annotationToDelete: Annotation | null = null;
    newAnnotationText = '';
    newAnnotationMentions: string[] = [];
    editBuffer: { [key: string]: string } = {};
    editMentions: { [key: string]: string[] } = {};
    showAll = false;
    private shouldFocusNewAnnotation = false;
    showTooltip: { [id: string]: boolean } = {};
    suppressBlur = false;

    mentionFlyoutOpen = false;
    mentionFlyoutPosition: { top: number; left?: number; right?: number; isAbove?: boolean } = { top: 0, left: 0 };
    mentionTarget: { type: 'new' | 'edit'; annotationId?: string } | null = null;
    mentionSuggestions: INameId[] = [];
    mentionSelectedIndex: number = -1; // Track selected index in mention dropdown

    constructor(private renderer: Renderer2) { }

    ngOnInit() {
        this.annotations = (this.annotations || []).map((a) => ({
            ...a,
            mentions: (a.mentions && a.mentions.length > 0) ? a.mentions : this.extractMentionIds(a.content),
        }));
    }

    ngAfterViewChecked() {
        if (this.shouldFocusNewAnnotation && this.newAnnotationTextarea) {
            this.newAnnotationTextarea.nativeElement.focus();
            this.shouldFocusNewAnnotation = false;
        }
        if (this.annotationTextEls) {
            this.annotationTextEls.forEach((elRef, idx) => {
                const el = elRef.nativeElement;
                const annotation = this.visibleAnnotations[idx];
                if (el && annotation) {
                    // 2 lines: lineHeight * 2 (21px * 2 = 42px, but allow for rounding)
                    this.showTooltip[annotation.id] = el.scrollHeight > el.clientHeight + 1;
                }
            });
        }
    }

    get visibleAnnotations(): Annotation[] {
        return this.showAll ? this.annotations : this.annotations.slice(0, 3);
    }

    canEdit(annotation: Annotation): boolean {
        if (this.disabled) {
            return false;
        }
        return annotation.createdBy === this.currentUserId;
    }

    showNewAnnotationInput() {
        this.newAnnotationFocused = true;
        this.shouldFocusNewAnnotation = true;
    }

    addAnnotation() {
        if (this.newAnnotationText.trim()) {
            const tokenizedContent = this.convertDisplayToTokens(this.newAnnotationText.trim(), this.newAnnotationMentions);
            const newAnnotation: Annotation = {
                id: Date.now().toString(), // Replace with real UUID in production
                content: tokenizedContent,
                createdBy: this.currentUserId,
                createdAt: new Date().toISOString(),
                projectId: this.projectId,
                taskId: this.taskId,
                taskName: this.taskName,
                workflowArea: this.workflowArea,
                mentions: this.extractMentionIds(tokenizedContent),
            };
            this.onAnnotationAdded.emit(newAnnotation);
            this.newAnnotationText = '';
            this.newAnnotationMentions = [];
        }
        this.newAnnotationFocused = false; // Hide input if empty
    }

    startEditing(annotation: Annotation): void {
        if (this.canEdit(annotation)) {
            this.editingId = annotation.id;
            this.editBuffer[annotation.id] = this.convertTokensToDisplay(annotation.content);
            const fallbackMentions = this.extractMentionIds(annotation.content);
            const mergedMentions = annotation.mentions && annotation.mentions.length > 0 ? annotation.mentions : fallbackMentions;
            this.editMentions[annotation.id] = [...mergedMentions];
            setTimeout(() => {
                if (this.editTextarea) {
                    this.editTextarea.nativeElement.focus();
                }
            });
        }
    }

    saveEdit(annotation: Annotation) {
        const tokenizedContent = this.convertDisplayToTokens(
            this.editBuffer[annotation.id].trim(),
            this.editMentions[annotation.id] ?? [],
        );
        const updated: Annotation = {
            ...annotation,
            content: tokenizedContent,
            taskName: this.taskName,
            workflowArea: this.workflowArea,
            mentions: this.extractMentionIds(tokenizedContent),
        };
        this.onAnnotationChanged.emit(updated);
        this.editingId = null;
    }

    cancelEdit() {
        this.editingId = null;
    }

    cancelAdd(): void {
        this.newAnnotationText = '';
        this.newAnnotationFocused = false;
        this.newAnnotationMentions = [];
    }

    remove(annotation: Annotation) {
        this.annotationToDelete = annotation;
        this.showDeleteModal = true;
    }

    cancelDelete() {
        this.annotationToDelete = null;
        this.showDeleteModal = false;
    }

    confirmDelete() {
        this.showDeleteModal = false;
        if (this.annotationToDelete) {
            this.onAnnotationRemoved.emit(this.annotationToDelete);
            this.annotationToDelete = null;
        }
    }

    getTruncatedContent(content: string | undefined, maxLength: number = 50): string {
        if (!content) return '';
        return content.length > maxLength ? content.slice(0, maxLength) + '...' : content;
    }
    endEdit(event: FocusEvent, annotation: Annotation): void {
        if (this.suppressBlur) {
            this.suppressBlur = false;
            return;
        }
        this.saveEdit(annotation);
    }

    onEnterKeyEdit(event: KeyboardEvent, annotation: Annotation): void {
        // If mention flyout is open, select the highlighted suggestion instead of saving
        if (this.mentionFlyoutOpen) {
            event.preventDefault();
            this.selectHighlightedMention();
            return;
        }
        if (!event.shiftKey) {
            event.preventDefault(); // Avoid newline
            this.saveEdit(annotation);
        }
    }

    endAdd(event: FocusEvent): void {
        if (this.suppressBlur) {
            this.suppressBlur = false;
            return;
        }
        this.addAnnotation();
    }

    onEnterKeyAdd(event: KeyboardEvent): void {
        // If mention flyout is open, select the highlighted suggestion instead of saving
        if (this.mentionFlyoutOpen) {
            event.preventDefault();
            this.selectHighlightedMention();
            return;
        }
        if (!event.shiftKey) {
            event.preventDefault(); // Avoid newline
            this.addAnnotation();
        }
    }

    onKeyDown(event: KeyboardEvent, target: 'new' | 'edit', annotationId?: string): void {
        if (!this.mentionFlyoutOpen) return;

        if (event.key === 'ArrowDown') {
            event.preventDefault();
            this.mentionSelectedIndex = Math.min(
                this.mentionSelectedIndex + 1,
                this.mentionSuggestions.length - 1
            );
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            this.mentionSelectedIndex = Math.max(this.mentionSelectedIndex - 1, 0);
        } else if (event.key === 'Escape') {
            event.preventDefault();
            this.closeMentionFlyout();
        } else if (event.key === 'Tab') {
            event.preventDefault();
            this.selectHighlightedMention();
        }
    }

    onMentionKey(event: KeyboardEvent, target: 'new' | 'edit', annotationId?: string): void {
        // Skip processing for arrow keys, Enter, and Escape as they're handled by onKeyDown
        if (['ArrowDown', 'ArrowUp', 'Enter', 'Escape'].includes(event.key)) {
            return;
        }

        const textarea = event.target as HTMLTextAreaElement;
        const cursorPos = textarea.selectionStart;
        const value = textarea.value;

        // Find the last @ before the cursor without a space in between
        const upToCursor = value.slice(0, cursorPos);
        const atIndex = upToCursor.lastIndexOf('@');
        if (atIndex === -1) {
            this.closeMentionFlyout();
            return;
        }

        const query = upToCursor.slice(atIndex + 1);
        if (query.includes(' ') || query.includes('\n')) {
            this.closeMentionFlyout();
            return;
        }

        const trimmedQuery = query.trim();
        const previousSuggestionsLength = this.mentionSuggestions.length;
        this.mentionSuggestions = this.users.filter((u) =>
            u.name.toLowerCase().startsWith(trimmedQuery.toLowerCase()) && u.id,
        );
        if (this.mentionSuggestions.length === 0) {
            this.closeMentionFlyout();
            return;
        }

        const rect = textarea.getBoundingClientRect();
        this.mentionFlyoutPosition = this.calculateFlyoutPosition(rect, 220, 240);
        this.mentionFlyoutOpen = true;
        
        // Only reset selection if suggestions changed (not just arrow key navigation)
        if (previousSuggestionsLength !== this.mentionSuggestions.length) {
            this.mentionSelectedIndex = 0;
        }
        
        this.mentionTarget = { type: target, annotationId };
    }

    getUserName(userId: string): string {
        const user = this.users.find((u) => u.id === userId);
        return user ? user.name : userId;
    }

    closeMentionFlyout(): void {
        this.mentionFlyoutOpen = false;
        this.mentionTarget = null;
        this.mentionSelectedIndex = -1;
        this.suppressBlur = false;
    }

    selectMention(user: INameId): void {
        if (!this.mentionTarget) return;
        const { type, annotationId } = this.mentionTarget;
        const targetRef = type === 'new' ? this.newAnnotationTextarea : this.editTextarea;
        if (!targetRef) return;
        const textarea = targetRef.nativeElement;
        const cursorPos = textarea.selectionStart;
        const value = textarea.value;
        const upToCursor = value.slice(0, cursorPos);
        const atIndex = upToCursor.lastIndexOf('@');
        if (atIndex === -1) return;

        const token = `@${user.name} `;
        const newContent = value.slice(0, atIndex) + token + value.slice(cursorPos);
        textarea.value = newContent;
        textarea.dispatchEvent(new Event('input'));

        const newPos = atIndex + token.length;
        textarea.setSelectionRange(newPos, newPos);
        textarea.focus();

        if (type === 'new') {
            if (!this.newAnnotationMentions.includes(user.id)) {
                this.newAnnotationMentions.push(user.id);
            }
        } else if (type === 'edit' && annotationId) {
            const list = this.editMentions[annotationId] ?? [];
            if (!list.includes(user.id)) {
                list.push(user.id);
                this.editMentions[annotationId] = list;
            }
        }

        this.closeMentionFlyout();
        this.suppressBlur = false;
    }

    private selectHighlightedMention(): void {
        if (this.mentionSelectedIndex < 0 || this.mentionSelectedIndex >= this.mentionSuggestions.length) {
            return;
        }

        this.selectMention(this.mentionSuggestions[this.mentionSelectedIndex]);
    }

    private calculateFlyoutPosition(triggerRect: DOMRect, flyoutWidth = 220, flyoutHeight = 240) {
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const padding = 8;

        let top = triggerRect.bottom + padding;
        let left: number | undefined = triggerRect.left;
        let right: number | undefined;
        let isAbove = false;

        if (triggerRect.left + flyoutWidth > viewportWidth - padding) {
            left = undefined;
            right = padding;
        }

        if (top + flyoutHeight > viewportHeight - padding) {
            // Position the top edge at (triggerRect.top - padding).
            // translateY(-100%) in the template then shifts the flyout upward by its
            // own actual rendered height, so the bottom edge sits flush just above the
            // textarea regardless of how many items are in the list.
            top = triggerRect.top - padding;
            isAbove = true;
        }

        return { top, left, right, isAbove };
    }

    onFlyoutMouseDown(event: MouseEvent): void {
        this.suppressBlur = true;
        event.stopPropagation();
        event.preventDefault();
    }

    formatContentForView(content: string): string {
        if (!content) return '';
        const regex = /@\[([^|\]]+)\|([^\]]+)\]/g;
        return content.replace(regex, (_match, name, id) => {
            const display = this.getUserName(id) || name;
            return `<span class="mention-chip-display"><b>@${display}</b></span>`;
        });
    }

    private convertTokensToDisplay(content: string): string {
        if (!content) return '';
        const regex = /@\[([^|\]]+)\|([^\]]+)\]/g;
        return content.replace(regex, (_m, name) => `@${name}`);
    }

    private convertDisplayToTokens(content: string, mentionIds: string[]): string {
        if (!content) return '';
        let result = content;
        const mentions = mentionIds
            .map((id) => ({ id, name: this.getUserName(id) }))
            .filter((m) => m.name)
            .sort((a, b) => b.name.length - a.name.length);

        for (const { id, name } of mentions) {
            const escaped = this.escapeRegExp(`@${name}`);
            const re = new RegExp(`${escaped}(?![\\w])`, 'g');
            result = result.replace(re, `@[${name}|${id}]`);
        }
        return result;
    }

    private escapeRegExp(text: string): string {
        return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }

    private extractMentionIds(content: string): string[] {
        if (!content) return [];
        const regex = /@\[[^|\]]+\|([^\]]+)\]/g;
        const ids = new Set<string>();
        let match: RegExpExecArray | null;
        while ((match = regex.exec(content)) != null) {
            ids.add(match[1]);
        }
        return Array.from(ids);
    }
}
